import React from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { AdminTab } from '@/components/dashboard/AdminTab';
import { CouncilTab } from '@/components/dashboard/CouncilTab';
import { CostsTab } from '@/components/dashboard/CostsTab';
import { HealthTab } from '@/components/dashboard/HealthTab';
import { MemoryTab } from '@/components/dashboard/MemoryTab';
import { SchedulerTab } from '@/components/dashboard/SchedulerTab';
import { SignalsTab } from '@/components/dashboard/SignalsTab';
import { TasksTab } from '@/components/dashboard/TasksTab';
import { WaitlistTab } from '@/components/dashboard/WaitlistTab';

// The legacy Command tab bar, preserved as data.
//
// Every mode that ever owned a tab keeps its mode, its label and its group, so
// the move to routed pages retires no destination (DOCKET-2026-10-21-05):
//
//   * `canonical` is where a mode resolves now. The ordinary modes keep their
//     own path under /command; the two blueprint-flagged modes (the operations
//     overview and the NotebookLM bridge) resolve to the preserved blueprint
//     content on /command/blueprints.
//   * `panel` is the existing panel, imported and mounted as it was. No panel
//     logic is copied here — this file only routes a mode to its panel.
//
// The rail renders the modes that still own a panel; the blueprint-compat modes
// stay reachable by their legacy URL and from /command/blueprints.

export type CommandGroup = 'Observe' | 'Operate' | 'Admin';

export interface LegacyCommand {
  /** The legacy `?mode=` value and the `/command/<mode>` path segment. */
  mode: string;
  /** The label preserved from the Command tab bar. */
  label: string;
  /** The tab group the mode belonged to. */
  group: CommandGroup;
  /** Where the mode resolves now. */
  canonical: string;
  /** The panel that owns the mode. Absent for the blueprint-compat modes. */
  panel?: React.ComponentType;
}

export const LEGACY_COMMANDS: LegacyCommand[] = [
  { mode: 'operations', label: 'Overview', group: 'Observe', canonical: '/command/blueprints' },
  { mode: 'health', label: 'Health', group: 'Observe', canonical: '/command/health', panel: HealthTab },
  { mode: 'signals', label: 'Signals', group: 'Observe', canonical: '/command/signals', panel: SignalsTab },
  { mode: 'memory', label: 'Codex Telemetry', group: 'Observe', canonical: '/command/memory', panel: MemoryTab },
  { mode: 'costs', label: 'Spend', group: 'Observe', canonical: '/command/costs', panel: CostsTab },
  { mode: 'council', label: 'Council', group: 'Observe', canonical: '/command/council', panel: CouncilTab },
  { mode: 'notebooklm', label: 'NotebookLM Bridge', group: 'Observe', canonical: '/command/blueprints' },
  { mode: 'scheduler', label: 'Scheduler', group: 'Operate', canonical: '/command/scheduler', panel: SchedulerTab },
  { mode: 'tasks', label: 'Tasks Overview', group: 'Operate', canonical: '/command/tasks', panel: TasksTab },
  { mode: 'customers', label: 'Customers/Tenants', group: 'Admin', canonical: '/command/customers', panel: AdminTab },
  { mode: 'waitlist', label: 'Waitlist', group: 'Admin', canonical: '/command/waitlist', panel: WaitlistTab },
];

// The panels keep their own internal scroll areas. The wrapper only stops a wide
// panel from breaking the page layout horizontally; on a phone the panel's
// viewport-height cap is lifted so the page itself scrolls.
const PANEL_SHELL =
  'overflow-x-auto rounded-lg border border-gray-800 bg-gray-900/20 ' +
  'max-sm:[&>div]:max-h-none max-sm:[&>div]:overflow-visible';

export function CommandLegacyPage() {
  const { mode } = useParams<{ mode: string }>();
  const command = LEGACY_COMMANDS.find((entry) => entry.mode === mode);

  if (!command) {
    return (
      <section aria-label="Unknown Command destination">
        <div className="mb-3 border-b border-gray-800 pb-2">
          <h2 className="text-sm font-mono uppercase tracking-[0.2em] text-white">Unknown destination</h2>
        </div>
        <p className="text-xs font-mono text-gray-400">
          No Command panel is mounted at <span className="text-gray-200">/command/{mode}</span>.
        </p>
        <Link
          to="/command"
          className="mt-3 inline-block text-xs font-mono uppercase tracking-wider text-[#39FF14] hover:underline"
        >
          Open the Command overview
        </Link>
      </section>
    );
  }

  if (!command.panel) {
    // A blueprint-compat mode keeps its legacy URL and lands on the preserved
    // blueprint content instead of a retired panel.
    return <Navigate to={command.canonical} replace />;
  }

  const Panel = command.panel;

  return (
    <section aria-label={command.label}>
      <div className={PANEL_SHELL}>
        <Panel />
      </div>
    </section>
  );
}
